import { Link } from "react-router-dom";
import { Flame } from "lucide-react";
import { articlePath, articleCat, formatRelativeDate, CATEGORY_LABEL } from "@/lib/readoo";

const TONES = [
  { card: "bg-brand text-onBrand border-brand", meta: "text-onBrand/75", num: "text-onBrand/30" },
  { card: "bg-onSurface text-surface border-onSurface", meta: "text-surface/70", num: "text-surface/25" },
  { card: "bg-brand/10 text-onSurface border-brand/30", meta: "text-subtle", num: "text-brand/35" },
];

function TopStoryCard({ a, i }) {
  const t = TONES[i % TONES.length];
  return (
    <Link
      to={articlePath(a)}
      data-testid={`top-story-${i}`}
      className={`group relative snap-start shrink-0 w-[82%] sm:w-auto flex flex-col justify-between gap-6 min-h-[220px] rounded-2xl border p-5 sm:p-6 overflow-hidden transition-transform duration-200 hover:-translate-y-0.5 ${t.card}`}
    >
      <span aria-hidden="true" className={`absolute -right-1 -bottom-7 font-serif font-semibold text-[6.5rem] leading-none select-none ${t.num}`}>
        {String(i + 1).padStart(2, "0")}
      </span>
      <div className="relative flex flex-col gap-3">
        <span className={`text-[11px] font-mono uppercase tracking-wider ${t.meta}`}>{CATEGORY_LABEL[articleCat(a)] || articleCat(a)}</span>
        <h3 className="font-serif text-[1.35rem] font-semibold leading-snug line-clamp-4 group-hover:underline decoration-2 underline-offset-4">
          {a.title}
        </h3>
      </div>
      <div className={`relative flex items-center gap-2 text-[11px] font-mono pr-20 ${t.meta}`}>
        {a.updates?.length > 0 && <span data-testid={`top-story-updated-${i}`} className="uppercase tracking-wider font-semibold">Aggiornato ·</span>}
        <span>{formatRelativeDate(a.published_at)}</span>
      </div>
    </Link>
  );
}

export function TopStories({ items }) {
  if (!items?.length) return null;
  return (
    <section data-testid="top-stories" className="space-y-4">
      <div className="flex items-baseline gap-3">
        <h2 className="flex items-center gap-2 font-serif text-xl sm:text-2xl font-semibold text-onSurface">
          <Flame size={20} className="text-brand" aria-hidden="true" /> In primo piano
        </h2>
        <span className="text-[11px] font-mono text-subtle hidden sm:inline">Le notizie più seguite delle ultime 24 ore</span>
      </div>
      <div className="-mx-4 px-4 sm:mx-0 sm:px-0 flex sm:grid sm:grid-cols-3 gap-4 sm:gap-5 overflow-x-auto sm:overflow-visible scroll-px-4 sm:scroll-px-0 snap-x snap-mandatory pb-1 [scrollbar-width:none]">
        {items.map((a, i) => <TopStoryCard key={a.article_id} a={a} i={i} />)}
      </div>
    </section>
  );
}

export function CategoryTopStory({ article: a, testid = "category-top-story" }) {
  if (!a) return null;
  return (
    <Link
      to={articlePath(a)}
      data-testid={testid}
      className="group relative block overflow-hidden rounded-2xl border border-brand bg-brand text-onBrand p-6 sm:p-8 mb-6 transition-transform duration-200 hover:-translate-y-0.5"
    >
      <span className="relative flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-onBrand/80 mb-4">
        <Flame size={14} aria-hidden="true" /> In primo piano
        {a.updates?.length > 0 && <span data-testid={`${testid}-updated`}>· Aggiornato</span>}
        <span className="normal-case tracking-normal text-onBrand/70">· {formatRelativeDate(a.published_at)}</span>
      </span>
      <h2 className="relative font-serif text-2xl sm:text-3xl lg:text-[2.25rem] font-semibold leading-tight group-hover:underline decoration-2 underline-offset-4">
        {a.title}
      </h2>
      {a.summary && <p className="relative mt-3 text-base sm:text-lg text-onBrand/85 leading-relaxed line-clamp-3">{a.summary}</p>}
    </Link>
  );
}
